import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, Skull, Shield, ArrowRight } from 'lucide-react';
import { useMafiaAuth } from '@/context/MafiaAuthContext';
import { useTranslation } from '@/i18n/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;
const RED = '#DC2626';

/**
 * Dual login page: Gamer (Google) and Host (email+password).
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function MafiaLoginPage() {
  const navigate = useNavigate();
  const { setHost } = useMafiaAuth();
  const { t } = useTranslation();
  const [mode, setMode] = useState('gamer'); // 'gamer' | 'host'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const onGoogle = () => {
    const clientId = '629466961731-k7kfjlb2mi559pquli3u2h9cl0k419ru.apps.googleusercontent.com';
    const redirect = window.location.origin + '/gamer/auth/callback';
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirect,
      response_type: 'code',
      scope: 'openid email profile',
      access_type: 'online',
      prompt: 'select_account',
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  const onHost = async (e) => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      const r = await fetch(`${API}/api/mafia/auth/host/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!r.ok) { setErr('Invalid credentials'); setBusy(false); return; }
      const data = await r.json();
      setHost && setHost(data.host);
      navigate('/host', { replace: true });
    } catch {
      setErr('Network error'); setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
      <div className="w-full max-w-md border border-white/10 bg-[#0A0303]">
        <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
          <Link to="/mafia" className="flex items-center gap-2 text-[#F5F5F5]">
            <div className="w-7 h-7 border border-[#DC2626] flex items-center justify-center">
              <Skull size={13} style={{ color: RED }} />
            </div>
            <span className="font-serif uppercase tracking-[0.3em] text-sm font-semibold">Mafia / Salon</span>
          </Link>
        </div>

        {/* Mode toggle */}
        <div className="grid grid-cols-2 border-b border-white/10">
          <button
            onClick={() => setMode('gamer')}
            data-testid="login-tab-gamer"
            className={`py-3 text-[11px] uppercase tracking-[0.3em] font-semibold transition-colors ${mode === 'gamer' ? 'bg-[#DC2626] text-white' : 'text-[#A3A3A3] hover:text-[#F5F5F5]'}`}
          >
            Gamer
          </button>
          <button
            onClick={() => setMode('host')}
            data-testid="login-tab-host"
            className={`py-3 text-[11px] uppercase tracking-[0.3em] font-semibold transition-colors ${mode === 'host' ? 'bg-[#DC2626] text-white' : 'text-[#A3A3A3] hover:text-[#F5F5F5]'}`}
          >
            Host
          </button>
        </div>

        <div className="p-6">
          {mode === 'gamer' && (
            <div className="space-y-5">
              <div>
                <h2 className="font-serif text-xl uppercase font-bold text-[#F5F5F5]">Sign in to play</h2>
                <p className="text-sm text-[#A3A3A3] mt-2">Continue with your Google account to claim your profile and earn points across games.</p>
              </div>
              <button
                onClick={onGoogle}
                data-testid="login-google-btn"
                className="w-full py-3 text-[12px] uppercase tracking-[0.25em] font-semibold bg-white text-black hover:bg-[#E5E5E5] transition-colors flex items-center justify-center gap-3"
              >
                <span className="font-sans tracking-normal text-base">G</span> Continue with Google
              </button>
              <p className="text-[11px] text-[#A3A3A3] text-center">No password. No spam. Just Google.</p>
            </div>
          )}

          {mode === 'host' && (
            <form onSubmit={onHost} className="space-y-4">
              <div>
                <h2 className="font-serif text-xl uppercase font-bold text-[#F5F5F5] flex items-center gap-2">
                  <Shield size={16} style={{ color: RED }} /> Host console
                </h2>
                <p className="text-sm text-[#A3A3A3] mt-2">Restricted area. Game control, analytics & scoring settings.</p>
              </div>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#A3A3A3]">Email</span>
                <div className="mt-1 flex items-center border border-white/15 focus-within:border-[#DC2626]">
                  <Mail size={14} className="ml-3 text-[#A3A3A3]" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="host-email-input"
                    required
                    className="w-full bg-transparent px-3 py-2.5 text-[#F5F5F5] text-sm outline-none"
                  />
                </div>
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#A3A3A3]">Password</span>
                <div className="mt-1 flex items-center border border-white/15 focus-within:border-[#DC2626]">
                  <Lock size={14} className="ml-3 text-[#A3A3A3]" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="host-password-input"
                    required
                    className="w-full bg-transparent px-3 py-2.5 text-[#F5F5F5] text-sm outline-none"
                  />
                </div>
              </label>
              {err && <p className="text-rose-400 text-xs" data-testid="host-login-error">{err}</p>}
              <button
                type="submit"
                disabled={busy}
                data-testid="host-login-btn"
                className="w-full py-3 text-[12px] uppercase tracking-[0.3em] font-semibold bg-[#DC2626] hover:bg-[#B91C1C] text-white transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {busy ? 'Signing in…' : 'Sign in'}<ArrowRight size={14} />
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
